import { Show, For, createMemo, createSignal, createEffect } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { qty, relTime, maskModelName } from '../lib/format.js';
import { tipProps } from '../components/MouseTooltip.jsx';
import wm from '../lib/wmStore.js';

/**
 * Live server perf + diagnostics widget (#48).
 * Panels (toggleable): process, latency, routes, cache, sessions, probe,
 * diagnostics (#247 — full system/discord/db dump), cacheDetail (#251 —
 * admin cache hit/miss/eviction counters).
 * Each stat has a tooltip explaining what it measures.
 */

const PANEL_DEFS = [
  { key: 'process',     label: 'Process'     },
  { key: 'latency',     label: 'Latency'     },
  { key: 'routes',      label: 'Routes'      },
  { key: 'cache',       label: 'Cache'       },
  { key: 'cacheDetail', label: 'Cache stats' },
  { key: 'sessions',    label: 'Sessions'    },
  { key: 'probe',       label: 'Probe'       },
  { key: 'diagnostics', label: 'Diagnostics' },
];

const TOOLTIPS = {
  uptime:    'How long the Node.js process has been running since last restart.',
  rss:       'Resident Set Size — total OS memory allocated to the process including shared libs.',
  heap:      'V8 heap used vs. total. High used/total ratio → consider restarting.',
  errors:    'Total errors logged since last metrics reset.',
  rpm:       'Requests per minute over the last 60 seconds.',
  p50:       'Median response time — 50% of requests complete within this.',
  p95:       'p95 response time — 95% of requests complete within this.',
  p99:       'p99 response time — only 1% of requests exceed this.',
  sampled:   'Total requests sampled in the current metrics window.',
  respCache: 'Response cache: hit rate for expensive query results.',
  privCache: 'Privacy cache: hit rate for the per-user privacy filter layer.',
  sseSess:   'Active SSE (live-feed) connections / maximum cap.',
  sessions:  'Active sessions in the SQLite session store.',
  probe:     'Uptime probe: external HTTP check against /api/healthz.',
  aiSession: 'Recent Fissal invocations from the ai_invocations log.',
  diag:      'Full system + Discord + DB diagnostics (port of v1 /admin diagnostics dump).',
  adminHits: 'Admin response cache: cumulative hits / misses / sets / evictions since process start.',
  cpuTemp:   'CPU package temperature read from /sys/class/thermal (Linux only).',
  dbInteg:   'Last SQLite integrity_check result. Cached for 10 minutes — click ↻ Diag to force a refresh.',
};

function Tip(props) {
  return (
    <span class="v2-perf-tip" tabIndex="0" aria-label={props.tip} {...tipProps(props.tip)}>?</span>
  );
}

function fmtUptime(sec) {
  const s = Number(sec) || 0;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtPct(n) {
  if (n == null) return '—';
  return (n * 100).toFixed(1) + '%';
}

// #270 — Skeleton placeholder used in stat values until first data() resolves.
// Keeps the widget chrome populated so users see fields immediately.
const SK = '·····';

export function ServerPerfPanel(props) {
  const topN = () => Math.min(Math.max(props.settings?.topRoutes || 8, 1), 25);

  // Panels visible — default to all. Settings can persist a comma-list.
  const defaultPanels = new Set(PANEL_DEFS.map(p => p.key));
  const [visiblePanels, setVisiblePanels] = createSignal(
    props.settings?.panels
      ? new Set(String(props.settings.panels).split(',').map(s => s.trim()).filter(Boolean))
      : new Set(defaultPanels),
  );

  function togglePanel(key) {
    setVisiblePanels(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      const wid = props.widget?.id;
      if (wid) {
        wm.setSettings(wid, {
          ...(props.widget?.settings || {}),
          panels: [...next].join(','),
        });
      }
      return next;
    });
  }

  const { data, loading, error, refetch } = useWidgetData('server-perf',
    () => ({ topRoutes: topN() }));

  const proc    = createMemo(() => data()?.process || {});
  const reqs    = createMemo(() => data()?.requests || {});
  const probe   = createMemo(() => data()?.probe || {});
  const routes  = createMemo(() => data()?.topRoutes || []);
  const caches  = createMemo(() => data()?.caches || null);
  const sessions= createMemo(() => data()?.sessions || null);
  const demand  = createMemo(() => data()?.demand || {});
  const recentAi= createMemo(() => data()?.recentAi || []);
  const show    = (key) => visiblePanels().has(key);

  // Lazy-loaded admin endpoints used by the Diagnostics + Cache stats
  // panels. They hit the existing v1 admin routes (which are admin-gated
  // and rate-limited at the express tier) so we don't have to duplicate
  // their logic in the widget runner. Fetch is gated on visibility and a
  // refresh tick so toggling a panel off doesn't keep polling.
  const [diag, setDiag]               = createSignal(null);
  const [diagErr, setDiagErr]         = createSignal('');
  const [diagLoading, setDiagLoading] = createSignal(false);
  const [diagTick, setDiagTick]       = createSignal(0);
  const [adminCache, setAdminCache]         = createSignal(null);
  const [adminCacheErr, setAdminCacheErr]   = createSignal('');
  const [adminCacheTick, setAdminCacheTick] = createSignal(0);

  async function loadDiag(force) {
    setDiagLoading(true);
    setDiagErr('');
    try {
      const url = '/api/admin/diagnostics' + (force ? '?refresh=1' : '');
      const res = await fetch(url, { credentials: 'same-origin' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setDiag(body);
    } catch (err) {
      setDiagErr(err?.message || 'Failed to load diagnostics.');
    } finally {
      setDiagLoading(false);
    }
  }

  async function loadAdminCache() {
    setAdminCacheErr('');
    try {
      const res = await fetch('/api/admin/cache-stats', { credentials: 'same-origin' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setAdminCache(body);
    } catch (err) {
      setAdminCacheErr(err?.message || 'Failed to load cache stats.');
    }
  }

  createEffect(() => {
    diagTick(); // dependency
    if (show('diagnostics')) loadDiag(false);
  });
  createEffect(() => {
    adminCacheTick();
    if (show('cacheDetail')) loadAdminCache();
  });

  return (
    <div class={`v2-system-panel v2-system-panel--perf ${props.class || ''}`}>
      <div class="v2-widget-hdr">
        {/* Panel filter chips */}
        <div class="v2-perf-filters">
          <For each={PANEL_DEFS}>{(p) => (
            <button
              class={`v2-perf-filter-chip ${show(p.key) ? 'is-on' : ''}`}
              onClick={() => togglePanel(p.key)}
            >{p.label}</button>
          )}</For>
        </div>
        <button class="v2-widget-hdr__btn" style={{ 'margin-left': 'auto' }}
          title="Refresh" onClick={() => refetch()}>↻</button>
      </div>
      <Show when={error()}>
        <div class="v2-error" style={{ margin: '4px 0' }}>{error()}</div>
      </Show>
      {/* #270 — render chrome + skeleton values immediately; do NOT gate the
          whole widget on the initial fetch. Values populate as data() arrives. */}
      <div class="v2-widget__scroll" aria-busy={loading() ? 'true' : 'false'}>

            {/* ── Process ── */}
            <Show when={show('process')}>
              <div class="v2-perf-section">
                <div class="v2-perf-section-label">Process</div>
                <div class="v2-stat-grid">
                  <div class="v2-stat">
                    <span class="v2-stat__label">Uptime <Tip tip={TOOLTIPS.uptime}/></span>
                    <span class="v2-stat__value">{data()?.uptimeSec != null ? fmtUptime(data().uptimeSec) : SK}</span>
                  </div>
                  <div class="v2-stat v2-stat--cyan">
                    <span class="v2-stat__label">RSS <Tip tip={TOOLTIPS.rss}/></span>
                    <span class="v2-stat__value">{proc().memMB ? proc().memMB + ' MB' : SK}</span>
                  </div>
                  <div class="v2-stat">
                    <span class="v2-stat__label">Heap <Tip tip={TOOLTIPS.heap}/></span>
                    <span class="v2-stat__value">
                      {proc().heapUsedMB ? `${proc().heapUsedMB}/${proc().heapTotalMB} MB` : SK}
                    </span>
                  </div>
                  <div class="v2-stat v2-stat--gold">
                    <span class="v2-stat__label">Errors <Tip tip={TOOLTIPS.errors}/></span>
                    <span class="v2-stat__value">{data() ? qty(data().errorCountTotal || 0) : SK}</span>
                  </div>
                  <div class="v2-stat">
                    <span class="v2-stat__label">RPM <Tip tip={TOOLTIPS.rpm}/></span>
                    <span class="v2-stat__value"
                      style={{ color: demand().label === 'high' ? 'var(--bad)' : demand().label === 'medium' ? 'var(--gold)' : 'inherit' }}>
                      {demand().rpm ?? SK} <Show when={demand().label}><span style={{ 'font-size': '10px', color: 'var(--t-green-dim)' }}>({demand().label})</span></Show>
                    </span>
                  </div>
                </div>
              </div>
            </Show>

            {/* ── Latency ── */}
            <Show when={show('latency')}>
              <div class="v2-perf-section">
                <div class="v2-perf-section-label">Latency</div>
                <div class="v2-stat-grid">
                  <div class="v2-stat">
                    <span class="v2-stat__label">p50 <Tip tip={TOOLTIPS.p50}/></span>
                    <span class="v2-stat__value">{data() ? (reqs().p50Ms ?? 0) + 'ms' : SK}</span>
                  </div>
                  <div class="v2-stat">
                    <span class="v2-stat__label">p95 <Tip tip={TOOLTIPS.p95}/></span>
                    <span class="v2-stat__value">{data() ? (reqs().p95Ms ?? 0) + 'ms' : SK}</span>
                  </div>
                  <div class="v2-stat">
                    <span class="v2-stat__label">p99 <Tip tip={TOOLTIPS.p99}/></span>
                    <span class="v2-stat__value">{data() ? (reqs().p99Ms ?? 0) + 'ms' : SK}</span>
                  </div>
                  <div class="v2-stat">
                    <span class="v2-stat__label">Sampled <Tip tip={TOOLTIPS.sampled}/></span>
                    <span class="v2-stat__value">{data() ? qty(reqs().totalSampled || 0) : SK}</span>
                  </div>
                </div>
              </div>
            </Show>

            {/* ── Cache ── */}
            <Show when={show('cache')}>
              <div class="v2-perf-section">
                <div class="v2-perf-section-label">Cache</div>
                <div class="v2-stat-grid">
                  <div class="v2-stat">
                    <span class="v2-stat__label">Resp hit <Tip tip={TOOLTIPS.respCache}/></span>
                    <span class="v2-stat__value">{caches() ? fmtPct(caches()?.response?.hitRate) : SK}</span>
                  </div>
                  <div class="v2-stat v2-stat--cyan">
                    <span class="v2-stat__label">Resp size</span>
                    <span class="v2-stat__value">{caches() ? qty(caches()?.response?.size ?? 0) : SK}</span>
                  </div>
                  <div class="v2-stat">
                    <span class="v2-stat__label">Priv hit <Tip tip={TOOLTIPS.privCache}/></span>
                    <span class="v2-stat__value">{caches() ? fmtPct(caches()?.privacy?.hitRate) : SK}</span>
                  </div>
                  <div class="v2-stat">
                    <span class="v2-stat__label">SSE <Tip tip={TOOLTIPS.sseSess}/></span>
                    <span class="v2-stat__value">
                      {caches() ? `${caches()?.sse?.clients ?? '—'} / ${caches()?.sse?.cap ?? '—'}` : SK}
                    </span>
                  </div>
                </div>
              </div>
            </Show>

            {/* ── Sessions ── */}
            <Show when={show('sessions')}>
              <div class="v2-perf-section">
                <div class="v2-perf-section-label">Current Sessions <Tip tip={TOOLTIPS.sessions}/></div>
                <div class="v2-stat-grid">
                  <div class="v2-stat">
                    <span class="v2-stat__label">Web sessions</span>
                    <span class="v2-stat__value">{sessions() ? qty(sessions()?.count ?? 0) : SK}</span>
                  </div>
                  <div class="v2-stat v2-stat--cyan">
                    <span class="v2-stat__label">SSE clients</span>
                    <span class="v2-stat__value">{caches() ? (caches()?.sse?.clients ?? '—') : SK}</span>
                  </div>
                </div>
                <Show when={recentAi().length > 0}>
                  <div class="v2-perf-section-label" style={{ 'margin-top': '8px' }}>
                    Recent AI Sessions <Tip tip={TOOLTIPS.aiSession}/>
                  </div>
                  <table class="v2-perf-table">
                    <thead><tr>
                      <th style={{ 'text-align': 'left' }}>User</th>
                      <th>Source</th><th>Model</th><th>Cost</th><th>When</th>
                    </tr></thead>
                    <tbody>
                      <For each={recentAi()}>{(r) => (
                        <tr>
                          <td class="v2-perf-route">{r.user_name || '—'}</td>
                          <td>{r.source || '—'}</td>
                          <td style={{ 'font-size': '9px' }}>{maskModelName(r.model || '—')}</td>
                          <td>${(r.cost || 0).toFixed(5)}</td>
                          <td style={{ 'font-size': '9px', color: 'var(--t-green-dim)' }}>{relTime(r.timestamp)}</td>
                        </tr>
                      )}</For>
                    </tbody>
                  </table>
                </Show>
              </div>
            </Show>

            {/* ── Routes ── */}
            <Show when={show('routes')}>
              <div class="v2-perf-section">
                <div class="v2-perf-section-label">Top Routes</div>
                <Show when={routes().length} fallback={<div class="v2-widget-skel">{loading() ? 'Loading routes…' : '—'}</div>}>
                  <table class="v2-perf-table">
                    <thead><tr>
                      <th style={{ 'text-align': 'left' }}>Route</th>
                      <th>n</th><th>avg</th><th>max</th>
                    </tr></thead>
                    <tbody>
                      <For each={routes()}>{(r) => (
                        <tr>
                          <td class="v2-perf-route" title={r.route}>{r.route}</td>
                          <td>{qty(r.count)}</td>
                          <td>{(r.avgMs || 0) + 'ms'}</td>
                          <td>{(r.maxMs || 0) + 'ms'}</td>
                        </tr>
                      )}</For>
                    </tbody>
                  </table>
                </Show>
              </div>
            </Show>

            {/* ── Probe ── */}
            <Show when={show('probe')}>
              <div class="v2-perf-section">
                <div class="v2-perf-section-label">Uptime Probe <Tip tip={TOOLTIPS.probe}/></div>
                <Show when={probe().lastCheckAt} fallback={<div class="v2-widget-skel">{loading() ? 'Loading probe…' : '—'}</div>}>
                  <div class="v2-perf-probe">
                    Status: <b>{probe().lastStatus || '—'}</b>
                    {' · '}{probe().lastDurMs != null ? probe().lastDurMs + 'ms' : '—'}
                    {' · '}fails {qty(probe().totalFailures || 0)}/{qty(probe().totalChecks || 0)}
                    {' · '}<span style={{ color: 'var(--t-green-dim)' }}>{relTime(probe().lastCheckAt)}</span>
                    <Show when={probe().lastError}>
                      <div class="v2-perf-probe__err">⚠ {probe().lastError}</div>
                    </Show>
                  </div>
                </Show>
              </div>
            </Show>

            {/* ── Cache Detail (#251) ── */}
            <Show when={show('cacheDetail')}>
              <div class="v2-perf-section">
                <div class="v2-perf-section-label" style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
                  Cache stats <Tip tip={TOOLTIPS.adminHits}/>
                  <button class="v2-widget-hdr__btn" style={{ 'margin-left': 'auto' }}
                    title="Refresh cache stats" onClick={() => setAdminCacheTick(t => t + 1)}>↻</button>
                </div>
                <Show when={adminCacheErr()}>
                  <div class="v2-error">{adminCacheErr()}</div>
                </Show>
                <Show when={!adminCacheErr() && adminCache()} fallback={<div class="v2-widget-skel">Loading…</div>}>
                  <div class="v2-perf-section-label" style={{ 'margin-top': '4px', 'font-size': '10px' }}>
                    Admin response cache (TTL 60s · invalidates on batchComplete)
                  </div>
                  <div class="v2-stat-grid">
                    <div class="v2-stat">
                      <span class="v2-stat__label">Hit rate</span>
                      <span class="v2-stat__value">{fmtPct(adminCache()?.admin?.hitRate)}</span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Hits</span>
                      <span class="v2-stat__value">{qty(adminCache()?.admin?.hits ?? 0)}</span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Misses</span>
                      <span class="v2-stat__value">{qty(adminCache()?.admin?.misses ?? 0)}</span>
                    </div>
                    <div class="v2-stat v2-stat--cyan">
                      <span class="v2-stat__label">Sets</span>
                      <span class="v2-stat__value">{qty(adminCache()?.admin?.sets ?? 0)}</span>
                    </div>
                    <div class="v2-stat v2-stat--gold">
                      <span class="v2-stat__label">Evictions</span>
                      <span class="v2-stat__value">{qty(adminCache()?.admin?.evictions ?? 0)}</span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Size / cap</span>
                      <span class="v2-stat__value">
                        {qty(adminCache()?.admin?.size ?? 0)} / {qty(adminCache()?.admin?.max ?? 0)}
                      </span>
                    </div>
                  </div>
                  <Show when={adminCache()?.note}>
                    <div class="v2-perf-probe" style={{ 'font-size': '10px', color: 'var(--t-green-dim)' }}>
                      {adminCache().note}
                    </div>
                  </Show>
                </Show>
              </div>
            </Show>

            {/* ── Diagnostics dump (#247) ── */}
            <Show when={show('diagnostics')}>
              <div class="v2-perf-section">
                <div class="v2-perf-section-label" style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
                  Diagnostics <Tip tip={TOOLTIPS.diag}/>
                  <button class="v2-widget-hdr__btn" style={{ 'margin-left': 'auto' }}
                    title="Force refresh (re-runs SQLite integrity_check)"
                    onClick={() => { setDiagTick(t => t + 1); loadDiag(true); }}>↻</button>
                </div>
                <Show when={diagErr()}>
                  <div class="v2-error">{diagErr()}</div>
                </Show>
                <Show when={!diagErr() && diag()} fallback={
                  <div class="v2-widget-skel">{diagLoading() ? 'Loading diagnostics…' : '—'}</div>
                }>
                  {/* System */}
                  <div class="v2-perf-section-label" style={{ 'margin-top': '4px', 'font-size': '10px' }}>System</div>
                  <div class="v2-stat-grid">
                    <div class="v2-stat">
                      <span class="v2-stat__label">Host</span>
                      <span class="v2-stat__value" style={{ 'font-size': '11px' }}>{diag().system?.hostname || '—'}</span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Platform</span>
                      <span class="v2-stat__value" style={{ 'font-size': '11px' }}>
                        {diag().system?.platform}/{diag().system?.arch}
                      </span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Node</span>
                      <span class="v2-stat__value" style={{ 'font-size': '11px' }}>{diag().system?.nodeVersion || '—'}</span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">OS uptime</span>
                      <span class="v2-stat__value">{fmtUptime(diag().system?.uptime)}</span>
                    </div>
                    <div class="v2-stat v2-stat--cyan">
                      <span class="v2-stat__label">CPU avg</span>
                      <span class="v2-stat__value"
                        style={{ color: (diag().system?.cpuUsage || 0) > 85 ? 'var(--bad)'
                                : (diag().system?.cpuUsage || 0) > 60 ? 'var(--gold)' : 'inherit' }}>
                        {diag().system?.cpuUsage ?? '—'}%
                      </span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">CPU cores</span>
                      <span class="v2-stat__value">{qty(diag().system?.cpuCores ?? 0)}</span>
                    </div>
                    <Show when={diag().system?.cpuTemp != null}>
                      <div class="v2-stat v2-stat--gold">
                        <span class="v2-stat__label">CPU temp <Tip tip={TOOLTIPS.cpuTemp}/></span>
                        <span class="v2-stat__value">{Math.round(diag().system.cpuTemp)}°C</span>
                      </div>
                    </Show>
                    <div class="v2-stat">
                      <span class="v2-stat__label">RAM used</span>
                      <span class="v2-stat__value">
                        {Math.round((diag().system?.usedMemory || 0) / 1048576)}
                        /{Math.round((diag().system?.totalMemory || 0) / 1048576)} MB
                      </span>
                    </div>
                  </div>

                  {/* Discord */}
                  <div class="v2-perf-section-label" style={{ 'margin-top': '8px', 'font-size': '10px' }}>Discord bot</div>
                  <div class="v2-stat-grid">
                    <div class="v2-stat">
                      <span class="v2-stat__label">Status</span>
                      <span class="v2-stat__value"
                        style={{ color: diag().discord?.connected ? 'var(--t-green)' : 'var(--bad)' }}>
                        {diag().discord?.gatewayStatus || (diag().discord?.connected ? 'READY' : 'DOWN')}
                      </span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Ping</span>
                      <span class="v2-stat__value">
                        {diag().discord?.ping != null ? diag().discord.ping + 'ms' : '—'}
                      </span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Guilds</span>
                      <span class="v2-stat__value">{qty(diag().discord?.guilds ?? 0)}</span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Bot uptime</span>
                      <span class="v2-stat__value">
                        {diag().discord?.uptime != null ? fmtUptime(diag().discord.uptime / 1000) : '—'}
                      </span>
                    </div>
                  </div>

                  {/* Databases */}
                  <div class="v2-perf-section-label" style={{ 'margin-top': '8px', 'font-size': '10px' }}>
                    Databases <Tip tip={TOOLTIPS.dbInteg}/>
                  </div>
                  <table class="v2-perf-table">
                    <thead><tr>
                      <th style={{ 'text-align': 'left' }}>Name</th>
                      <th>Size</th><th>Tables</th><th>Integrity</th>
                    </tr></thead>
                    <tbody>
                      <For each={['sales', 'ai']}>{(k) => (
                        <tr>
                          <td class="v2-perf-route" title={diag().databases?.[k]?.path}>{k}</td>
                          <td>{Math.round((diag().databases?.[k]?.size || 0) / 1048576)} MB</td>
                          <td>{qty(diag().databases?.[k]?.tables ?? 0)}</td>
                          <td style={{ color: diag().databases?.[k]?.integrity ? 'var(--t-green)' : 'var(--bad)' }}>
                            {diag().databases?.[k]?.integrity ? 'ok' : 'fail'}
                          </td>
                        </tr>
                      )}</For>
                    </tbody>
                  </table>

                  {/* Sync service */}
                  <div class="v2-perf-section-label" style={{ 'margin-top': '8px', 'font-size': '10px' }}>Sync service</div>
                  <div class="v2-stat-grid">
                    <div class="v2-stat">
                      <span class="v2-stat__label">Processing</span>
                      <span class="v2-stat__value">{diag().syncService?.isProcessing ? 'yes' : 'idle'}</span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Queue</span>
                      <span class="v2-stat__value">{qty(diag().syncService?.queueLength ?? 0)}</span>
                    </div>
                    <div class="v2-stat v2-stat--gold">
                      <span class="v2-stat__label">Stale?</span>
                      <span class="v2-stat__value"
                        style={{ color: diag().syncService?.isStale ? 'var(--bad)' : 'inherit' }}>
                        {diag().syncService?.isStale ? 'yes' : 'no'}
                      </span>
                    </div>
                    <div class="v2-stat">
                      <span class="v2-stat__label">Last MM</span>
                      <span class="v2-stat__value" style={{ 'font-size': '10px', color: 'var(--t-green-dim)' }}>
                        {diag().syncService?.lastSync ? relTime(diag().syncService.lastSync) : '—'}
                      </span>
                    </div>
                  </div>
                </Show>
              </div>
            </Show>

          </div>
    </div>
  );
}

export default function ServerPerf(props) {
  return <ServerPerfPanel {...props} class="v2-widget" />;
}
